import React, { useEffect, useState, useRef } from "react";
import { IoMdArrowDropdown, IoMdArrowDropup } from "react-icons/io";
import { MdOutlineRefresh } from "react-icons/md";
import {
    fetchStationFeeder,
    fetchStationFeederList,
    commitFeeder,
    fetchStationFeederLoad,
} from "./api";
import Table from "./table";
import "./feeder.css";
import { restrictedUsers } from "./restrictedUsers";

const Feeder = () => {
    const user = JSON.parse(localStorage.getItem("user"));
    const isRestrictedUser = restrictedUsers.includes(user?.email);
    const [loading, setLoading] = useState(false);
    const [allData, setAllData] = useState([]);

    const [selectedDiscom, setSelectedDiscom] = useState(
        localStorage.getItem("selectedDiscom") || "Select Discom"
    );
    const [selectedDistrict, setSelectedDistrict] = useState(
        localStorage.getItem("selectedDistrict") || "Select District"
    );
    const [selectedVoltage, setSelectedVoltage] = useState(
        localStorage.getItem("selectedVoltage") || "Select Voltage Ratio"
    );
    const [selectedSubstation, setSelectedSubstation] = useState(
        localStorage.getItem("selectedSubstation") || "Select Substation"
    );

    const [isFirstLoad, setIsFirstLoad] = useState(true);
    const [discomOptions, setDiscomOptions] = useState([]);
    const [districtOptions, setDistrictOptions] = useState([]);
    const [voltageOptions, setVoltageOptions] = useState([]);
    const [substationOptions, setSubstationOptions] = useState([]);

    const [headers, setHeaders] = useState([]);
    const [tableData, setTableData] = useState([]);
    const tableRef = useRef(null);
    const [statusMessage, setStatusMessage] = useState("");
    const [messageType, setMessageType] = useState("");
    const [openDropdown, setOpenDropdown] = useState(null);
    const [selectedDate, setSelectedDate] = useState(() => {
        const now = new Date();
        const istOffset = 5.5 * 60 * 60 * 1000;
        const istDate = new Date(now.getTime() + istOffset);
        istDate.setDate(istDate.getDate() - 1);
        return istDate.toISOString().split("T")[0];
    });
    const [feederList, setFeederList] = useState([]);
    const [stickyCols, setStickyCols] = useState(3);

    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth < 600) {
                setStickyCols(0);
            } else if (window.innerWidth < 1024) {
                setStickyCols(1);
            } else {
                setStickyCols(3);
            }
        };

        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        if (statusMessage) {
            const timer = setTimeout(() => setStatusMessage(""), 3000);
            return () => clearTimeout(timer);
        }
    }, [statusMessage]);


    useEffect(() => {
        const fetchDropdownData = async () => {
            setLoading(true);
            try {
                const res = await fetchStationFeeder();
                const feederData = res.data || res;
                setAllData(feederData);

                setDiscomOptions([...new Set(feederData.map((item) => item.discom))]);
            } catch (err) {
                console.error("Error fetching feeder data:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchDropdownData();
    }, []);

    useEffect(() => {
        setLoading(true);
        try {
            if (
                selectedDiscom &&
                selectedDiscom !== "Select Discom" &&
                selectedSubstation &&
                selectedSubstation !== "Select Substation"
            ) {
                fetchFeederList(selectedDiscom, selectedSubstation);
            }
        } catch (err) {
            console.error("Error fetching feeder data:", err);
        } finally {
            setLoading(false);
        }
    }, []);

    const handleSelect = (type, value) => {
        if (type === "discom") {
            setSelectedDiscom(value);
            localStorage.setItem("selectedDiscom", value);

            const filtered = allData.filter((item) => item.discom === value);
            setDistrictOptions([...new Set(filtered.map((i) => i.district))]);
            setSubstationOptions([...new Set(filtered.map((i) => i.substation))]);
            setVoltageOptions([...new Set(filtered.map((i) => i.voltage_ratio))]);

            setSelectedDistrict("Select District");
            setSelectedSubstation("Select Substation");
            setSelectedVoltage("Select Voltage Ratio");
            setHeaders([]);
            setTableData([]);
        }
        if (type === "district") {
            setSelectedDistrict(value);
            localStorage.setItem("selectedDistrict", value);
        }
        if (type === "substation") {
            setSelectedSubstation(value);
            localStorage.setItem("selectedSubstation", value);
        }
        if (type === "voltage") {
            setSelectedVoltage(value);
            localStorage.setItem("selectedVoltage", value);
        }

        if (
            type === "substation" &&
            value !== "Select Substation" &&
            selectedDiscom !== "Select Discom"
        ) {
            fetchFeederList(selectedDiscom, value);
        }

        setOpenDropdown(null);
    };

    const fetchFeederList = async (discom, substation) => {
        if (
            !selectedDate?.trim() ||
            !selectedDiscom?.trim() ||
            !selectedSubstation?.trim() ||
            discom === "Select Substation" ||
            substation === "Select Discom"
        ) {
            return;
        }
        setLoading(true);
        try {
            const res = await fetchStationFeederList(discom, substation);
            const list = res.data || res;
            setFeederList(list);
            processFeederData(list);
        } catch (err) {
            console.error("Error fetching feeder list:", err);
        } finally {
            setLoading(false);
        }
    };

    const fetchFeederLoad = async (discom, substation) => {
        if (
            !selectedDate?.trim() ||
            !selectedDiscom?.trim() ||
            !selectedSubstation?.trim() ||
            discom === "Select Substation" ||
            substation === "Select Discom"
        ) {
            return;
        }
        setLoading(true);
        try {
            const res = await fetchStationFeederLoad(selectedDate, discom, substation);
            const list = res.data || res;
            setFeederList(list);
            processFeederLoadData(list);
        } catch (err) {
            console.error("Error fetching feeder load:", err);
            setHeaders([]);
            setTableData([]);
            fetchFeederList(discom, substation);
        } finally {
            setLoading(false);
        }
    };

    const processFeederData = (response) => {
        if (!Array.isArray(response) || response.length === 0) return;

        const headersWithMetadata = [
            { name: "feeder", dataType: "string", editable: false, width: 200, displayName: "Feeder" },
            { name: "feeder_code", dataType: "string", editable: false, width: 180, displayName: "Feeder Code" },
            { name: "feeder_category", dataType: "string", editable: false, width: 150, displayName: "Feeder Category" },
            ...Array.from({ length: 24 }, (_, i) => ({
                name: `col${i + 1}`,
                dataType: "number",
                editable: true,
                width: 100,
                displayName: `${i + 1}`,
            })),
            { name: "energy_consumable", dataType: "number", editable: true, width: 150, displayName: "Energy Consumable (kWh)" },
        ];

        const rows = response.map((row) => {
            const newRow = {
                feeder: row.feeder,
                feeder_code: row.feeder_code,
                feeder_category: row.feeder_category,
            };
            for (let i = 1; i <= 26; i++) newRow[`col${i}`] = "";
            newRow["energy_consumable"] = "";
            return newRow;
        });

        setHeaders(headersWithMetadata);
        setTableData(rows);
    };

    const processFeederLoadData = (response) => {
        if (!Array.isArray(response) || response.length === 0) return;

        setTableData((prevTableData) => {
            const updatedTable = prevTableData.map((row) => ({ ...row }));

            response.forEach((loadRow) => {
                const matchingRow = updatedTable.find(
                    (r) => r.feeder === loadRow.feeder || r.feeder_code === loadRow.feeder_code
                );
                if (!matchingRow) return;

                matchingRow.voltage_ratio = loadRow.voltage_ratio;

                const hourlyArr = loadRow.hourly_load
                    .split(",")
                    .map((val) => (val === null || val === undefined || val.trim() === "" ? " " : val.trim()));

                for (let i = 0; i < 24; i++) {
                    matchingRow[`col${i + 1}`] = hourlyArr[i] ?? " ";
                }

                matchingRow.energy_consumable = loadRow.energy_consumption ?? " ";
            });

            return updatedTable;
        });
    };

    const Dropdown = ({ label, options, type, selected, disabled = false }) => {
        const [searchTerm, setSearchTerm] = useState("");

        const toggleOpen = () => {
            if (disabled) return;  // 🚫 block opening if disabled
            setOpenDropdown(openDropdown === type ? null : type);
            setSearchTerm("");
        };

        const filteredOptions = options.filter(
            (opt) => opt && opt.toLowerCase().includes(searchTerm.toLowerCase())
        );

        const isOpen = openDropdown === type;

        return (
            <div className={`feed-dropdown-wrapper ${disabled ? "disabled" : ""}`}>
                <button
                    className="feed-dropdown-button"
                    onClick={toggleOpen}
                    disabled={disabled}  // 🚫 native HTML disable
                >
                    {selected} {isOpen ? <IoMdArrowDropup /> : <IoMdArrowDropdown />}
                </button>

                {!disabled && isOpen && (   // 🚫 don’t render dropdown if disabled
                    <div className="feed-dropdown-menu">
                        {options.length > 10 && (
                            <input
                                type="text"
                                placeholder="Search..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="feed-dropdown-search"
                            />
                        )}
                        <div className="feed-dropdown-options">
                            {filteredOptions.map((opt, idx) => (
                                <div
                                    key={idx}
                                    className={`feed-dropdown-item ${selected === opt ? "selected" : ""}`}
                                    onClick={() => handleSelect(type, opt)}
                                >
                                    {opt}
                                </div>
                            ))}
                            {filteredOptions.length === 0 && (
                                <div className="feed-dropdown-item no-results">No results found</div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const formatDataForAPI = (tableData, headers) => {
        const formattedData = [];
        if (
            !selectedDate?.trim() ||
            !selectedDiscom?.trim() ||
            !selectedSubstation?.trim() ||
            selectedSubstation === "Select Substation" ||
            selectedDiscom === "Select Discom"
        ) {
            return;
        }

        tableData.forEach((row) => {
            if (!row || !row.feeder?.trim() || !row.feeder_code?.trim()) return;

            const colHeaders = headers.map((h) => h.name).filter((name) => name.startsWith("col"));
            const hourlyValues = colHeaders.map((col) => {
                let val = row[col];
                return val === null || val === undefined || val === "" || val === "null" ? " " : val;
            });

            const hourlyLoadString = hourlyValues.join(",");
            const energyVal =
                row.energy_consumable === null || row.energy_consumable === undefined || row.energy_consumable === ""
                    ? null
                    : Number(row.energy_consumable);

            const districtVal = selectedDistrict === "Select District" ? "" : selectedDistrict;
            const voltageVal = selectedVoltage === "Select Voltage Ratio" ? "" : selectedVoltage;

            formattedData.push([
                selectedDate,
                selectedDiscom,
                districtVal,
                selectedSubstation,
                voltageVal,
                row.feeder,
                hourlyLoadString,
                energyVal,
            ]);
        });

        return formattedData;
    };

    const handleCommit = async () => {
        setLoading(true);
        try {
            const updatedTableData = tableRef.current?.getUpdatedData();
            if (!updatedTableData || updatedTableData.length === 0) {
                setStatusMessage("No updated table data available.");
                setMessageType("error");
                return;
            }

            const loadData = formatDataForAPI(updatedTableData, headers);
            if (loadData.length === 0) {
                setStatusMessage("No valid Load Data to commit.");
                setMessageType("error");
                return;
            }

            const responses = await Promise.all([commitFeeder(loadData)]);
            const hasError = responses.some((res) => res.status !== 200);
            if (hasError) {
                setStatusMessage("Some API calls failed.");
                setMessageType("error");
            } else {
                setStatusMessage("Data committed successfully!");
                setMessageType("success");
                if (selectedDiscom && selectedSubstation) {
                    fetchFeederLoad(selectedDiscom, selectedSubstation);
                }
            }
        } catch (error) {
            setStatusMessage("Commit API Call Failed.");
            setMessageType("error");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="feed-positionmap-container">
            {loading && <div className="dashboard-loading-bar"></div>}

            <div className="feed-top-bar">
                <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                    <div className="date-picker">
                        <input
                            id="date-input"
                            type="date"
                            value={selectedDate}
                            onChange={(e) => setSelectedDate(e.target.value)}
                        />
                        <label htmlFor="date-input">Date</label>
                    </div>

                    <Dropdown
                        label={selectedDiscom}
                        options={discomOptions.length ? discomOptions : ["Select Discom"]}
                        type="discom"
                        selected={selectedDiscom}
                    />
                    <Dropdown
                        label={selectedDistrict}
                        options={districtOptions.length ? districtOptions : ["Select District"]}
                        type="district"
                        selected={selectedDistrict}
                        disabled={isRestrictedUser}
                    />
                    <Dropdown
                        label={selectedVoltage}
                        options={voltageOptions.length ? voltageOptions : ["Select Voltage Ratio"]}
                        type="voltage"
                        selected={selectedVoltage}
                        disabled={isRestrictedUser}
                    />
                    <Dropdown
                        label={selectedSubstation}
                        options={substationOptions.length ? substationOptions : ["Select Substation"]}
                        type="substation"
                        selected={selectedSubstation}
                    />
                               <button
                    className="feed-refresh-button"
                    onClick={() => {
                        if (selectedDiscom && selectedSubstation) {
                            fetchFeederLoad(selectedDiscom, selectedSubstation);
                        }
                    }}
                >
                    <MdOutlineRefresh className="button-icon" size={24} />
                </button>
                </div>

     
            </div>

            {statusMessage && <div className={`status-message ${messageType}`}>{statusMessage}</div>}

            {tableData.length > 0 && (
                <Table
                    ref={tableRef}
                    headers={headers}
                    rows={tableData}
                    edit={true}
                    sorting={true}
                    drag={true}
                    filtering={true}
                    exporting={true}
                    addRow={false}
                    restrictEditToNewRows={false}
                    stickyColumns={stickyCols}
                    defaultRowsPerPage={20}
                />
            )}

            <div className="feed-commit-container">
                <div className="feed-commit" onClick={handleCommit}>
                    COMMIT
                </div>
            </div>
        </div>
    );

};

export default Feeder;
